import { z } from 'zod';
import { isValidCep, normalizeCep } from '@/domain/cep';
import type { Coords } from '@/domain/geo';

export type GeocodeOk = {
  status: 'ok';
  coords: Coords;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
};
export type GeocodeFailure = {
  status: 'invalid_cep' | 'cep_not_found' | 'imprecise' | 'unavailable';
};
export type GeocodeResult = GeocodeOk | GeocodeFailure;

const VIA_CEP_BASE = 'https://viacep.com.br/ws';
const PHOTON_BASE = 'https://photon.komoot.io/api/';

/* Ask for several candidates and keep the one whose city matches, rather than trusting
   the first. Photon used to be biased to the São Paulo centre, on the premise that every
   station sat in Greater São Paulo. The network expands city by city, and measured on
   2026-08-26 that bias dragged "Avenida Brasil, Campinas" and "Rua Bahia, Campinas" onto
   the São Paulo streets of the same name, where the city guard rejected them and the lead
   read "we could not locate this CEP". The query already names the city and the state, so
   ranking by text answered 12 of 13 cases against 10, and lost none in the metro area. */
const PHOTON_CANDIDATES = 5;

// ViaCEP sends '' for a field it cannot fill, so a blank value carries no address.
const nonBlankString = z.string().refine((value) => value.trim() !== '');

const finiteNumber = z.number().refine((value) => Number.isFinite(value));

// ViaCEP answers 200 with this body when the CEP does not exist. Some records carry
// the flag as the string 'true' instead of the boolean.
const viaCepNotFoundSchema = z.object({ erro: z.union([z.literal(true), z.literal('true')]) });

/* A whole-municipality CEP has no street and no neighbourhood, so those fall back to ''.
   The municipality and the state are what the Photon query and the city guard need, so a
   body without both is unusable. */
const viaCepAddressSchema = z
  .object({
    logradouro: z.string().catch(''),
    bairro: z.string().catch(''),
    localidade: nonBlankString,
    uf: nonBlankString,
  })
  .transform((body) => ({
    street: body.logradouro,
    neighborhood: body.bairro,
    city: body.localidade,
    state: body.uf,
  }));

type PostalAddress = z.infer<typeof viaCepAddressSchema>;

const photonFeatureSchema = z.object({
  geometry: z.object({
    // GeoJSON stores coordinates as [lon, lat]. Swapping them puts São Paulo in the
    // Atlantic. A third element is the altitude, which this page does not use.
    coordinates: z.tuple([finiteNumber, finiteNumber]).rest(z.number()),
  }),
  // Photon puts the municipality in a different field depending on the OSM object it hit.
  properties: z.object({
    city: z.string().optional(),
    district: z.string().optional(),
    county: z.string().optional(),
  }),
});

const photonResponseSchema = z.object({ features: z.array(photonFeatureSchema) });

type PhotonFeature = z.infer<typeof photonFeatureSchema>;

// 'São Paulo' and 'Sao Paulo' are the same municipality, so drop diacritics and case.
const normalizeName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const buildPhotonUrl = (address: PostalAddress): string => {
  const parts = [address.street, address.neighborhood, address.city, address.state, 'Brasil'].filter(
    (part) => part.trim() !== '',
  );
  const query = encodeURIComponent(parts.join(', '));
  return `${PHOTON_BASE}?q=${query}&limit=${PHOTON_CANDIDATES}`;
};

/* The comparison is strict equality on the normalized name, never a substring: 'São Paulo'
   must not accept 'São Paulo de Olivença', 900 km away. A coordinate this guard cannot
   vouch for never reaches the UI, because a wrong distance band is worse than no answer. */
const cityMatches = (feature: PhotonFeature, expectedCity: string): boolean => {
  const expected = normalizeName(expectedCity);
  if (expected === '') {
    return false;
  }
  const { city, district, county } = feature.properties;
  return [city, district, county].some(
    (name) => name !== undefined && normalizeName(name) === expected,
  );
};

export const geocodeCep = async (
  rawCep: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GeocodeResult> => {
  if (!isValidCep(rawCep)) {
    return { status: 'invalid_cep' };
  }
  try {
    const cepResponse = await fetchImpl(`${VIA_CEP_BASE}/${normalizeCep(rawCep)}/json/`);
    if (!cepResponse.ok) {
      return { status: 'unavailable' };
    }
    const cepBody: unknown = await cepResponse.json();
    if (viaCepNotFoundSchema.safeParse(cepBody).success) {
      return { status: 'cep_not_found' };
    }
    const address = viaCepAddressSchema.safeParse(cepBody);
    if (!address.success) {
      return { status: 'unavailable' };
    }
    const photonResponse = await fetchImpl(buildPhotonUrl(address.data));
    if (!photonResponse.ok) {
      return { status: 'unavailable' };
    }
    const photonBody = photonResponseSchema.safeParse(await photonResponse.json());
    if (!photonBody.success) {
      return { status: 'imprecise' };
    }
    const feature = photonBody.data.features.find((candidate) =>
      cityMatches(candidate, address.data.city),
    );
    if (feature === undefined) {
      return { status: 'imprecise' };
    }
    const [lon, lat] = feature.geometry.coordinates;
    return { status: 'ok', coords: { lat, lon }, ...address.data };
  } catch {
    return { status: 'unavailable' };
  }
};
